import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'common/components/button/Button';
import { WizardFooterSlotContext } from 'common/components/wizardFooterSlot/WizardFooterSlot';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import FamiliesService, { ImportFamiliesResult } from 'api/families/FamiliesService';
import Toast from 'common/services/Toast';
import Loading from 'common/services/Loading';
import StepUpload from '../importElements/steps/StepUpload';
import StepMapping from './steps/StepMapping';
import { ExcelSheetData, FamilyColumnMappingEntry } from './utils/types';
import { buildImportContent } from './utils/contentBuilder';
import styles from './ImportFamiliesScreen.module.scss';

function ImportFamiliesScreen (): JSX.Element {
    const { t } = useTranslation();

    // Wizard state
    const [step, setStep] = useState(0);
    // Footer DOM node that the mapping step portals its table pagination into.
    const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);
    const [excelData, setExcelData] = useState<ExcelSheetData | null>(null);
    const [fileName, setFileName] = useState('');
    const [headerRowNumber, setHeaderRowNumber] = useState(1);
    const [columnMappings, setColumnMappings] = useState<FamilyColumnMappingEntry[]>([]);

    // Template state
    const [templateFamilyId, setTemplateFamilyId] = useState<string | null>(null);
    const [templateSubFamilyId, setTemplateSubFamilyId] = useState<string | null>(null);

    // Import state
    const [isImporting, setIsImporting] = useState(false);
    const [result, setResult] = useState<ImportFamiliesResult | null>(null);

    const initMappingData = useCallback((data: ExcelSheetData, headerRow: number) => {
        const headerRowIdx = headerRow - 1;
        const headers = data.rows[headerRowIdx] ?? [];

        const mappings: FamilyColumnMappingEntry[] = headers.map((cell, idx) => ({
            excelColumnIndex: idx,
            excelColumnName: cell,
            mappedField: null,
        }));

        setColumnMappings(mappings);
    }, []);

    const onFileLoaded = useCallback((data: ExcelSheetData, name: string) => {
        setExcelData(data);
        setFileName(name);
        initMappingData(data, headerRowNumber);
        setStep(1);
    }, [headerRowNumber, initMappingData]);

    const onHeaderRowChange = useCallback((row: number) => {
        setHeaderRowNumber(row);
        if (excelData) {
            initMappingData(excelData, row);
        }
    }, [excelData, initMappingData]);

    const onColumnMappingChange = useCallback((colIndex: number, field: string | null) => {
        setColumnMappings(prev => prev.map(m =>
            m.excelColumnIndex === colIndex
                ? { ...m, mappedField: field }
                : m
        ));
    }, []);

    const onError = useCallback((message: string) => {
        Toast.error(message);
    }, []);

    const handleImport = async () => {
        if (!templateFamilyId) {
            Toast.warning(t('families.import_wizard.select_template_family'));
            return;
        }

        if (!excelData) return;

        const content = buildImportContent(excelData.rows, columnMappings, headerRowNumber);

        if (!content.trim()) {
            Toast.warning(t('families.import_wizard.no_rows_to_import'));
            return;
        }

        try {
            setIsImporting(true);
            Loading.show();
            setStep(2);

            const response = await FamiliesService.import({
                templateFamilyId,
                templateSubFamilyId: templateSubFamilyId ?? undefined,
                content
            });

            setResult(response);

            if (response.errors.length === 0) {
                Toast.success(t('families.import_wizard.import_success', { count: response.totalCreated }));
            } else {
                Toast.warning(t('families.import_wizard.import_partial', { created: response.totalCreated, errors: response.errors.length }));
            }
        } catch {
            Toast.error(t('common.messages.unexpected_error'));
            setStep(1);
        } finally {
            setIsImporting(false);
            Loading.hide();
        }
    };

    const handleReset = () => {
        setStep(0);
        setExcelData(null);
        setFileName('');
        setColumnMappings([]);
        setHeaderRowNumber(1);
        setTemplateFamilyId(null);
        setTemplateSubFamilyId(null);
        setResult(null);
    };

    const hasFamilyMapped = columnMappings.some(m => m.mappedField === 'family');

    return (
        <WizardFooterSlotContext.Provider value={footerSlot}>
        <ScreenTitle title={t('families.import_wizard.title')}>
            <PageHeader title={t('families.import_wizard.title')} informationText={t('families.import_wizard.subtitle')} />

            <PageContainer className={styles.pageContainer}>
                {/* Step indicators */}
                <div className={styles.steps}>
                    <div className={`${styles.step} ${step >= 0 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>1</span>
                        <span className={styles.stepLabel}>{t('families.import_wizard.step_upload')}</span>
                    </div>
                    <div className={styles.stepDivider} />
                    <div className={`${styles.step} ${step >= 1 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>2</span>
                        <span className={styles.stepLabel}>{t('families.import_wizard.step_mapping')}</span>
                    </div>
                    <div className={styles.stepDivider} />
                    <div className={`${styles.step} ${step >= 2 ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>3</span>
                        <span className={styles.stepLabel}>{t('families.import_wizard.step_import')}</span>
                    </div>
                </div>

                <div className={styles.content}>
                    {/* Step 0: Upload */}
                    {step === 0 && (
                        <StepUpload
                            excelData={excelData}
                            fileName={fileName}
                            onFileLoaded={onFileLoaded}
                            onError={onError}
                        />
                    )}

                    {/* Step 1: Mapping */}
                    {step === 1 && excelData && (
                        <StepMapping
                            excelData={excelData}
                            headerRowNumber={headerRowNumber}
                            columnMappings={columnMappings}
                            templateFamilyId={templateFamilyId}
                            templateSubFamilyId={templateSubFamilyId}
                            onTemplateFamilyChange={setTemplateFamilyId}
                            onTemplateSubFamilyChange={setTemplateSubFamilyId}
                            onColumnMappingChange={onColumnMappingChange}
                            onHeaderRowChange={onHeaderRowChange}
                        />
                    )}

                    {/* Step 2: Results */}
                    {step === 2 && result && (
                        <div className={`${styles.resultContainer} ${result.errors.length > 0 ? styles.partial : styles.success}`}>
                            <h5>{t('families.import_wizard.import_result')}</h5>
                            <p>
                                <strong>{t('families.import_wizard.total_created')}:</strong> {result.totalCreated}<br />
                                <strong>{t('families.import_wizard.families_created')}:</strong> {result.familiesCreated}<br />
                                <strong>{t('families.import_wizard.sub_families_created')}:</strong> {result.subFamiliesCreated}
                            </p>

                            {result.errors.length > 0 && (
                                <div style={{ color: '#856404' }}>
                                    <strong>{t('families.import_wizard.errors_label')} ({result.errors.length}):</strong>
                                    <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {result.errors.map((err, i) => (
                                            <li key={i}>{err}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.createdFamilies.length > 0 && (
                                <div>
                                    <strong>{t('families.import_wizard.created_families_list')}:</strong>
                                    <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {result.createdFamilies.map((f) => (
                                            <li key={f.id}>
                                                {f.isSubFamily ? '↳ ' : ''}{f.name}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigation buttons (with table pagination portaled into the centre) */}
                <div className={styles.footer}>
                    <div className={styles.footerSide}>
                        {step === 1 && (
                            <Button
                                variant="secondary"
                                onClick={() => setStep(0)}
                            >
                                {t('families.import_wizard.previous')}
                            </Button>
                        )}
                    </div>
                    <div ref={setFooterSlot} className={styles.footerCenter} />
                    <div className={`${styles.footerSide} ${styles.footerSideEnd}`}>
                        {step === 0 && (
                            <Button
                                onClick={() => setStep(1)}
                                disabled={!excelData}
                            >
                                {t('families.import_wizard.next')}
                            </Button>
                        )}
                        {step === 1 && (
                            <Button
                                onClick={handleImport}
                                disabled={!hasFamilyMapped || !templateFamilyId || isImporting}
                            >
                                {t('families.import_wizard.confirm_import')}
                            </Button>
                        )}
                        {step === 2 && result && (
                            <Button onClick={handleReset}>
                                {t('families.import_wizard.new_import')}
                            </Button>
                        )}
                    </div>
                </div>
            </PageContainer>
        </ScreenTitle>
        </WizardFooterSlotContext.Provider>
    );
}

export default ImportFamiliesScreen;
